import React, { ReactElement, useEffect, useState } from "react";

import { ISectionLogger } from "@multiweb/logging";
import { isEmptyArray, isEmptyString } from "@multiweb/reactUtils";
import { CaseInsensitiveMap } from "@multiweb/types/multiweb";
import { IComponentSize } from "@multiweb/types/react";
import { classNames } from "./constants";
import { MeasuredItem } from "./MeasuredItem";
import { IMeasuredItem } from "./types";

interface IMeasurerProps {
    /** Kolekce komoponent, u kterých chceme změřit rozměry po vyrenderování */
    itemsToMeasure: ReadonlyArray<IMeasuredItem>;
    /** Callback, který se zavolá v momentě kdy jsou změřeny všechny požadované komponenty */
    onAllMeasuredCallback: () => void;
    /** Logger (volitelně) */
    logger?: ISectionLogger;
}

/** Komponenta postyktuje měření výšek a šířek pro předaný seznam komponent */
export const Measurer: React.FC<IMeasurerProps> = ({
    itemsToMeasure,
    onAllMeasuredCallback,
    logger,
}) => {
    const [itemsMeasured, setItemsMeasured] = useState<CaseInsensitiveMap<IComponentSize>>(new CaseInsensitiveMap<IComponentSize>());
    const [allMeasuredTriggered, setAllMeasuredTriggered] = useState<boolean>(false);
    const itemMeasuredHandler = (item: IMeasuredItem, size: IComponentSize) => {
        logger?.debug("items measured", item.key, size.width);
        item.onItemMeasured(size);
        if (isEmptyString(item.key))
            logger?.error("Cannot measure item without valid key");

        setItemsMeasured(prev => {
            const newList = new CaseInsensitiveMap<IComponentSize>(prev);
            if (newList.has(item.key))
                logger?.error("Found one or more items with same measure keys. This will probably break measuring sizes.");
            else
                newList.set(item.key, size);

            return newList;
        });
    };

    //Pokud už byly změřeny všechny komponenty, je potřeba vyvolat callback, že už bylo vše změřeno
    useEffect(() => {
        if (!allMeasuredTriggered && !isEmptyArray(itemsToMeasure) && itemsMeasured.size === itemsToMeasure.length) {
            onAllMeasuredCallback();
            setAllMeasuredTriggered(true);
            logger?.debug("All items were measured.");
        }
        //Stačí mi hook volat jen při změně itemsMeasured.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsMeasured]);

    const [items, setItems] = useState<ReactElement[] | undefined>();
    useEffect(() => {
        setItems(itemsToMeasure && itemsToMeasure.map((item) =>
            <MeasuredItem key={item.key} onMeasuredCallback={(size) => itemMeasuredHandler(item, size)}>
                {item.component}
            </MeasuredItem>));
        setItemsMeasured(new CaseInsensitiveMap());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsToMeasure]);

    return (
        <div className={classNames()}>
            {items}
        </div>
    );
};
